export const SURVEY_IMMUTABLE_AFTER_FIRST_TRANSMIT =
  'First Survey has been transmitted. First and Final cannot be deleted or reset. To abandon this job, Delete Workspace. To change a completed Final, use Reopen Final Survey.';

export const COMMERCIAL_DELETE_BLOCKED_AFTER_FIRST_TRANSMIT =
  'First Survey has been transmitted. This quote cannot be deleted. Delete Workspace to remove the entire job, including surveys, versions, and customer transmissions.';

export function surveySheetDeleteConfirmMessage(surveyLabel: string): string {
  const name = String(surveyLabel || '').trim() || 'Survey';
  return `Are you sure you want to delete ${name}? This action cannot be undone.`;
}

export function firstSurveyHasBeenTransmitted(args: {
  firstVersionIsTransmitted?: boolean | null;
  anyCommercialVersionIsTransmitted?: boolean | null;
  hasFirstArtefactShareLink?: boolean | null;
  hasFinalSurvey?: boolean | null;
}): boolean {
  return !!(
    args.firstVersionIsTransmitted
    || args.anyCommercialVersionIsTransmitted
    || args.hasFirstArtefactShareLink
    || args.hasFinalSurvey
  );
}

/**
 * After First transmission, neither First nor Final may be deleted or reset.
 * Before transmission, First may still be cleared.
 */
export function surveySheetDeleteBlockedReason(args: {
  kind?: string | null;
  firstHasBeenTransmitted: boolean;
}): string | null {
  const kind = String(args.kind || '').trim().toLowerCase();
  if (kind !== 'first' && kind !== 'final') return null;
  if (args.firstHasBeenTransmitted) return SURVEY_IMMUTABLE_AFTER_FIRST_TRANSMIT;
  return null;
}

/** @deprecated Use surveySheetDeleteBlockedReason — lock is First transmission, not Final existence. */
export function firstSurveyDeleteBlockedReason(firstHasBeenTransmitted: boolean): string | null {
  return surveySheetDeleteBlockedReason({ kind: 'first', firstHasBeenTransmitted });
}

export function commercialDocumentDeleteBlockedReason(firstHasBeenTransmitted: boolean): string | null {
  return firstHasBeenTransmitted ? COMMERCIAL_DELETE_BLOCKED_AFTER_FIRST_TRANSMIT : null;
}

/**
 * First Survey is permanently historical after its first successful customer transmission.
 * Final existence is a consequence of that transmit, not the lock trigger.
 */
export function firstSurveyIsHistorical(firstIsTransmitted: boolean): boolean {
  return !!firstIsTransmitted;
}

export function firstSurveyHistoricalLockReason(firstIsTransmitted: boolean): string | null {
  return firstIsTransmitted
    ? 'First Survey is permanently immutable after transmission. All further changes belong on Final Survey.'
    : null;
}

/** Chip on Visual Designer: only after this First sheet's version was actually sent. */
export function firstSurveyShowsTransmittedLock(args: {
  surveyKind?: string | null;
  firstVersionIsTransmitted?: boolean | null;
}): boolean {
  return String(args.surveyKind || '').trim().toLowerCase() === 'first'
    && !!args.firstVersionIsTransmitted;
}

/**
 * Final Survey tab:
 * - Quote Accepted/Cash (quote-first path)
 * - Draft/sent quote rolled from an accepted estimate (estimate-first path)
 * Estimates never have a Final stage. First transmit alone must not open Final.
 */
export function finalSurveyTabAvailable(args: {
  hasFinalSurvey?: boolean | null;
  firstIsTransmitted?: boolean | null;
  quoteStatus?: string | null;
  documentKind?: string | null;
  rolledFromAcceptedEstimate?: boolean | null;
  /** Final work already begun stays reachable even if the quote status moves off Accepted/Cash. */
  finalWorkStarted?: boolean | null;
}): boolean {
  void args.hasFinalSurvey;
  void args.firstIsTransmitted;
  const kind = String(args.documentKind || '').trim();
  if (kind && kind !== 'Quotation') return false;
  if (args.rolledFromAcceptedEstimate) return true;
  if (args.finalWorkStarted) return true;
  const normalized = String(args.quoteStatus || '').trim().toLowerCase();
  return normalized === 'accepted' || normalized === 'cash';
}

/** An accepted/cash quote cannot go back to Draft or Sent once Final Survey work has begun. */
export function quoteStatusRevertBlocked(args: {
  documentKind?: string | null;
  fromStatus?: string | null;
  toStatus?: string | null;
  finalWorkPhase: FinalSurveyWorkPhase;
}): boolean {
  if (String(args.documentKind || '').trim() !== 'Quotation') return false;
  const from = String(args.fromStatus || '').trim().toLowerCase();
  const to = String(args.toStatus || '').trim().toLowerCase();
  if (from !== 'accepted' && from !== 'cash') return false;
  if (to !== 'draft' && to !== 'sent') return false;
  return args.finalWorkPhase !== 'not_started';
}

export const QUOTE_STATUS_REVERT_BLOCKED_MESSAGE =
  'Final Survey work has started on this quote, so it can no longer go back to Draft or Sent.';

/** @deprecated Use finalSurveyTabAvailable — Final remains gated on Accept/Cash. */
export function quoteStatusAllowsFinalSurvey(status?: string | null): boolean {
  return finalSurveyTabAvailable({ quoteStatus: status });
}

/** Lightweight Final work phase — not customer-facing versions. */
export type FinalSurveyWorkPhase = 'not_started' | 'in_progress' | 'complete';

export function finalSurveyWorkPhase(survey: {
  completionStatus?: string | null;
  workStartedAt?: string | null;
  reopenedAt?: string | null;
} | null | undefined): FinalSurveyWorkPhase {
  if (!survey) return 'not_started';
  if (String(survey.completionStatus || '').toLowerCase() === 'complete') return 'complete';
  if (survey.workStartedAt || survey.reopenedAt) return 'in_progress';
  return 'not_started';
}
